import type { FeedEmailPreference } from '@parcel/types';
import { Button, Checkbox, Skeleton, cn } from '@parcel/ui';
import { AppLogo, type AppLogoName } from '@parcel/ui/app-logo';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import {
  useNotificationPreferenceMutation,
  useNotificationPreferences,
} from '../../lib/notification-preferences.js';
import { isSupabaseConfigured } from '../../lib/supabase.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../../lib/workspaces.js';
import { LockedFeaturePanel } from '../billing/locked-feature-panel.js';
import { IntegrationTile } from '../integration-card.js';
import { SettingsPageHeader, SettingsPanel, SettingsShell } from './settings-shell.js';

const CHAT_APPS: { logo: AppLogoName; name: string; description: string }[] = [
  {
    logo: 'slack',
    name: 'Slack',
    description: 'Get updates on the accounts and projects you follow directly in Slack.',
  },
  {
    logo: 'microsoft-teams',
    name: 'Microsoft Teams',
    description: 'Get updates on the accounts and projects you follow directly in Microsoft Teams.',
  },
];

const CHANNELS = ['Slack', 'Teams', 'Email'] as const;

// Title, then one fixed column per channel; the header row shares it.
const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_repeat(3,3.5rem)] items-center gap-x-4';

/** Personal settings: how this member hears about changes on what the workspace follows. */
export function NotificationSettingsPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id;
  const preferences = useNotificationPreferences(workspaceId);
  const save = useNotificationPreferenceMutation(workspaceId);

  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Personal"
        title="Notifications"
        description="Choose how you hear about the accounts and projects you follow: a daily email digest today, and soon Slack and Microsoft Teams."
      />
      <div className="space-y-10">
        <ChatApps />
        {preferences.isLoading || workspaceQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : preferences.isError ? (
          <p className="text-sm text-muted-foreground">
            Couldn't load your notification settings.{' '}
            <button
              type="button"
              className="underline underline-offset-2"
              onClick={() => void preferences.refetch()}
            >
              Retry
            </button>
          </p>
        ) : (
          <FeedSection
            current={preferences.data?.feed_email}
            emailAllowed={preferences.data?.email_allowed !== false}
            saving={save.isPending}
            onSave={(value) => save.mutate(value)}
            workspaceSlug={workspaceSlug}
          />
        )}
      </div>
    </SettingsShell>
  );
}

function ChatApps() {
  return (
    <SettingsPanel className="divide-y">
      {CHAT_APPS.map((app) => (
        <div key={app.logo} className="flex items-center gap-3 p-4">
          <IntegrationTile>
            <AppLogo name={app.logo} size={20} aria-hidden />
          </IntegrationTile>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-medium">{app.name}</h3>
            <p className="text-sm text-muted-foreground">{app.description}</p>
          </div>
          <Button type="button" variant="outline" size="sm" disabled>
            Coming soon
          </Button>
        </div>
      ))}
    </SettingsPanel>
  );
}

function FeedSection({
  current,
  emailAllowed,
  saving,
  onSave,
  workspaceSlug,
}: {
  current: FeedEmailPreference | undefined;
  emailAllowed: boolean;
  saving: boolean;
  onSave: (value: FeedEmailPreference) => void;
  workspaceSlug: string | null;
}) {
  // Every plan gets the email (Free a counts-only one), so every member can turn it off.
  const emailOn = current !== undefined && current !== 'off';

  return (
    <section aria-labelledby="feed-notifications-heading" className="space-y-2">
      <div className={cn(ROW_GRID, 'pr-4')}>
        <h3 id="feed-notifications-heading" className="text-sm font-medium">
          News feed
        </h3>
        {CHANNELS.map((channel) => (
          <span key={channel} className="text-center text-xs text-muted-foreground">
            {channel}
          </span>
        ))}
      </div>
      <SettingsPanel>
        <div className={cn(ROW_GRID, 'p-4')}>
          <div className="min-w-0">
            <p className="text-sm font-medium">Daily digest</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {emailAllowed
                ? 'Everything new on what you follow, each morning at 7 AM ET.'
                : 'How many updates there are on what you follow, each morning at 7 AM ET.'}
            </p>
          </div>
          <ComingSoonCell channel="Slack" />
          <ComingSoonCell channel="Teams" />
          <span className="flex justify-center">
            <Checkbox
              aria-label="Email the daily digest"
              checked={emailOn}
              disabled={saving}
              onCheckedChange={(checked) => onSave(checked === true ? 'daily' : 'off')}
            />
          </span>
        </div>
      </SettingsPanel>
      {!emailAllowed && workspaceSlug ? (
        <LockedFeaturePanel
          feature="Full daily digest"
          gate="email_alerts"
          requiredPlan="starter"
          benefit="See which projects and accounts changed and what's new on each, right in your inbox."
          workspaceSlug={workspaceSlug}
        />
      ) : null}
    </section>
  );
}

function ComingSoonCell({ channel }: { channel: string }) {
  return (
    <span className="flex justify-center text-muted-foreground">
      <span aria-hidden>—</span>
      <span className="sr-only">{channel}: coming soon</span>
    </span>
  );
}
